import React, { useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SettingsSectionCard from './SettingsSectionCard';
import ClientPolicySettingsPanel from './ClientPolicySettingsPanel';
import ClientTagsSettingsPanel from './ClientTagsSettingsPanel';
import ClientTagRulesSettingsPanel from './ClientTagRulesSettingsPanel';
import { CLIENT_MANAGEMENT_TABS, getClientManagementPath } from '../../utils/settingsForm';

export default function ClientManagementSettingsSection({
  form,
  setField,
  onSave,
  onReset,
  loading,
  saving,
  error,
  success,
  setSuccess,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const tabParam = searchParams.get('tab') || 'policies';
  const activeTab = CLIENT_MANAGEMENT_TABS.includes(tabParam) ? tabParam : 'policies';

  useEffect(() => {
    if (tabParam && !CLIENT_MANAGEMENT_TABS.includes(tabParam)) {
      navigate(getClientManagementPath('policies'), { replace: true });
    }
  }, [tabParam, navigate]);

  const tabOptions = {
    policies: t('settings.clientManagement.tabs.policies'),
    tags: t('settings.clientManagement.tabs.tags'),
    tagRules: t('settings.clientManagement.tabs.tagRules'),
  };

  const sharedPanelProps = {
    form,
    setField,
    onSave,
    onReset,
    saving,
  };

  const renderPanel = () => {
    switch (activeTab) {
      case 'tags':
        return <ClientTagsSettingsPanel {...sharedPanelProps} />;
      case 'tagRules':
        return <ClientTagRulesSettingsPanel {...sharedPanelProps} />;
      case 'policies':
      default:
        return <ClientPolicySettingsPanel {...sharedPanelProps} />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <SettingsSectionCard
      title={t('settings.sections.clientManagement')}
      description={t('settings.clientManagement.description')}
      error={error}
      success={success}
      successMessage={t('settings.saved')}
      onCloseSuccess={() => setSuccess(false)}
    >
      <Box>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          {tabOptions[activeTab]}
        </Typography>
        {renderPanel()}
      </Box>
    </SettingsSectionCard>
  );
}
